import {
  IPDB_CONTROL_WELLS_LAYER,
  IPDB_CONTROL_WELLS_LAYER_TYPE
} from "constants/mapLayers.constants";
import { AnyLayer } from "mapbox-gl";

import { IOrderedLayer } from "../useManageMapboxLayerSorting";

interface IGetMapLayersMatchingWithLayerListParams {
  mapboxLayers: AnyLayer[];
  layerList: IOrderedLayer[];
}

/**
 * True if a mapbox layer corresponds to an entry in the layer list, e.g. a direct
 * name/projectLayerType match, or one of a shapefile's variant layers (-stroke, -point, etc).
 */
export function doesMapboxLayerMatchOrderedLayer(
  layer: AnyLayer,
  orderedLayer: IOrderedLayer
): boolean {
  return Boolean(
    // Shapefile rows carry a user-supplied name, so a shapefile called e.g.
    // "County" would otherwise claim the grid layer of the same name. Those rows
    // match on their id prefix below instead.
    (!orderedLayer.shapefileId &&
      !orderedLayer.projectShapefileId &&
      orderedLayer.name === layer.id) ||
      // Handle IPDB layers - match by projectLayerType
      orderedLayer.projectLayerType === layer.id ||
      // Handle IPDB Control Wells - special case for name mismatch
      (layer.id === IPDB_CONTROL_WELLS_LAYER &&
        orderedLayer.projectLayerType === IPDB_CONTROL_WELLS_LAYER_TYPE) ||
      // Shapefile variant layers are the shapefile id plus a suffix, e.g. {id}-stroke
      (orderedLayer.projectShapefileId &&
        layer.id.startsWith(orderedLayer.projectShapefileId)) ||
      (orderedLayer.shapefileId && layer.id.startsWith(orderedLayer.shapefileId))
  );
}

/**
 * Retrieves the mapbox layers that match the layer list, potentially unordered,
 * so they can be used to compared to the ordered layer list.
 * @param params
 * @returns
 */
export function getMapLayersMatchedToLayerList(
  params: IGetMapLayersMatchingWithLayerListParams
): IOrderedLayer[] {
  const { mapboxLayers, layerList } = params;
  const matchedMapLayers = mapboxLayers
    .filter((layer) => {
      return layerList.some((orderedLayer) =>
        doesMapboxLayerMatchOrderedLayer(layer, orderedLayer)
      );
    })
    .map((layer, index) => ({ name: layer.id, order: index }));
  return matchedMapLayers;
}
